import type React from 'react'
import { useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import { InternalUseOnlyCheckbox } from '../InternalUseOnlyCheckbox'
import EditTemplateTasks from './EditTemplateTasks'
import { FFInput } from '~/shared/ui'
import { CarePlanTemplateItem, CarePlanModalTask } from './useCarePlanModal'
import SelectTemplate from './SelectTemplate'
import { CarePlanTemplatesPreviewRetrieveResponse } from '@fireflyhealth/api'
import type { PatientUser } from '~/shared/types'
import type { RootState } from '~/app/store/configureStore'

interface ICreateFromTemplateProps {
  patient: PatientUser
  me: RootState['me']
  tasks: CarePlanModalTask[]
  setTasks: (arg0: CarePlanModalTask[]) => void
  title: string
  notes: string
  setTitle: (arg0: string) => void
  setNotes: (arg0: string) => void
  selectedTemplate: CarePlanTemplateItem | null
  selectTemplate: (carePlanTemplateId: number | null, patient: PatientUser) => void
  previewData?: CarePlanTemplatesPreviewRetrieveResponse
  previewDataLoading: boolean
  previewDataError?: unknown
  carePlanTemplatesLoading: boolean
  loadCarePlanTemplates: () => void
  carePlanTemplates?: CarePlanTemplateItem[]
  setAllTemplateTasksComplete: (arg0: boolean) => void
  saving: boolean
  existingCarePlanTypeIds: { [key: number]: boolean }
  internalOnly: boolean
  setInternalOnly: (value: boolean) => void
}

const CreateFromTemplate = (props: ICreateFromTemplateProps) => {
  const {
    selectedTemplate,
    selectTemplate,
    previewData,
    previewDataLoading,
    previewDataError,
    carePlanTemplatesLoading,
    loadCarePlanTemplates,
    carePlanTemplates,
    setAllTemplateTasksComplete,
    internalOnly,
    setInternalOnly,
  } = props

  useEffect(() => {
    if (!carePlanTemplates) {
      loadCarePlanTemplates()
    }
  }, [carePlanTemplates, loadCarePlanTemplates])

  if (!selectedTemplate) {
    return (
      <SelectTemplate
        carePlanTemplates={carePlanTemplates}
        selectedTemplate={selectedTemplate}
        carePlanTemplatesLoading={carePlanTemplatesLoading}
        selectTemplate={templateId => selectTemplate(templateId, props.patient)}
        existingCarePlanTypeIds={props.existingCarePlanTypeIds}
      />
    )
  }

  const handleUpdateNotes = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.setNotes(event.target.value)
  }

  const handleUpdateTitle = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    props.setTitle(event.target.value)
  }
  const taskPreviews = (): React.JSX.Element => {
    if (!previewData) return <></>
    if (previewDataError) {
      return <Typography>There was a problem loading the preview for this template</Typography>
    }
    if (!previewData.task_previews?.length) {
      setAllTemplateTasksComplete(true)
      return <Typography>No tasks associated with this template</Typography>
    }
    return (
      <>
        <FFInput
          disabled={props.saving}
          onChange={e => handleUpdateTitle(e)}
          value={props.title}
          placeholder="Add care plan name"
          fullWidth
        />
        <Box sx={{ mt: 1 }}>
          <FFInput
            disabled={props.saving}
            onChange={e => handleUpdateNotes(e)}
            value={props.notes}
            placeholder="Add description"
            multiline={true}
            fullWidth
          />
        </Box>
        <Box sx={{ mt: 1, mb: 1 }}>
          <InternalUseOnlyCheckbox
            checked={internalOnly}
            onChange={setInternalOnly}
            disabled={props.saving}
          />
        </Box>
        <EditTemplateTasks
          patient={props.patient}
          me={props.me}
          previewData={previewData}
          setAllTemplateTasksComplete={setAllTemplateTasksComplete}
          saving={props.saving}
          tasks={props.tasks}
          setTasks={props.setTasks}
        />
      </>
    )
  }

  return (
    <>
      <Box>
        {previewDataLoading && <CircularProgress />}
        {!previewDataLoading && <Box sx={{ ml: 2 }}>{taskPreviews()}</Box>}
      </Box>
    </>
  )
}

export default CreateFromTemplate
